import React, { useContext, useReducer, useMemo } from 'react'

const reducer = (state, action) => {
  switch (action.type) {
    case 'SET_PAGE':
      return {
        ...state,
        pages: {
          ...state.pages,
          [action.payload.path]: action.payload.data,
        },
      }

    case 'SET_CACHED_PAGE':
      return {
        ...state,
        mergedPagesCache: {
          ...state.mergedPagesCache,
          [action.payload.path]: action.payload.data,
        },
      }

    case 'ENABLE_PREVIEWS':
      return { ...state, previewsEnabled: true }

    case 'DISABLE_PREVIEWS':
      return { ...state, previewsEnabled: false }

    case 'TOGGLE_PREVIEWS':
      return { ...state, previewsEnabled: !state.previewsEnabled }

    case 'SET_SHARE_LINK':
      return { ...state, shareLink: action.payload.link }
  }
}

const PrismicPreviewStoreStateContext = React.createContext()
const PrismicPreviewStoreActionsContext = React.createContext()

export const PrismicPreviewStoreProvider = ({ children }) => {
  const [state, dispatch] = useReducer(reducer, {
    previewsEnabled: true,
    pages: {},
    mergedPagesCache: {},
    shareLink: undefined,
  })

  // Derived State
  const numPreviewedPages = Object.keys(state.pages).length
  const hasPreviews = numPreviewedPages > 0
  const getPreviewDataForPath = path => state.pages[path]
  const hasPreviewDataForPath = path => state.pages.hasOwnProperty(path)
  const getCachedDataForPath = path => state.mergedPagesCache[path]
  const hasCachedDataForPath = path =>
    state.mergedPagesCache.hasOwnProperty(path)

  // Actions
  const setPage = (path, data) =>
    dispatch({ type: 'SET_PAGE', payload: { path, data } })
  const setCachedPage = (path, data) =>
    dispatch({ type: 'SET_CACHED_PAGE', payload: { path, data } })
  const enablePreviews = () => dispatch({ type: 'ENABLE_PREVIEWS' })
  const disablePreviews = () => dispatch({ type: 'DISABLE_PREVIEWS' })
  const togglePreviews = () => dispatch({ type: 'TOGGLE_PREVIEWS' })
  const setShareLink = link =>
    dispatch({ type: 'SET_SHARE_LINK', payload: { link } })

  const actions = useMemo(
    () => ({
      setPage,
      setCachedPage,
      enablePreviews,
      disablePreviews,
      togglePreviews,
      setShareLink,
    }),
    [],
  )

  return (
    <PrismicPreviewStoreStateContext.Provider
      value={{
        ...state,

        numPreviewedPages,
        hasPreviews,
        getPreviewDataForPath,
        hasPreviewDataForPath,
        getCachedDataForPath,
        hasCachedDataForPath,
      }}
    >
      <PrismicPreviewStoreActionsContext.Provider value={actions}>
        {children}
      </PrismicPreviewStoreActionsContext.Provider>
    </PrismicPreviewStoreStateContext.Provider>
  )
}

export const usePrismicPreviewStoreState = () =>
  useContext(PrismicPreviewStoreStateContext)
export const usePrismicPreviewStoreActions = () =>
  useContext(PrismicPreviewStoreActionsContext)
